/* Lamp configurator – see blocks/ai_gen_block_d76e2b8.liquid for markup and rules overview. */
(() => {
  if (customElements.get('lamp-configurator')) return;

  const ROCKET_SKIPS = ['balls', 'figure', 'brocade'];
  const CLOUD_SHAPES = ['cloud', '3d_cloud'];
  const CHEERS = [
    'Godt valg! 🌟',
    'Uh, den bliver fin! ✨',
    'Det kan vi godt lide! 💛',
    'Fantastisk – videre til næste! 🚀',
    'Perfekt! 🎨',
    'Sikke et øje for design! 👀',
  ];

  /* Answers left out of the marketing events: the child's name, contact details and free text. */
  const PRIVATE_ANSWERS = ['Lampens navn', 'Kontakt e-mail', 'Kontakt telefon', 'Kommentar'];

  /* Lamp drawing (snippets/lamp-art.liquid). Coordinates are in its 600 × 400 viewBox. */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const PALETTE_SIZE = 5;
  const BODY_COLOR = '#F7F4EE';
  const LIGHT_SPACING = 40;
  const LIGHT_OFFSET = 9;
  const BALL_RADII = [22, 18, 24, 17, 21, 19, 23, 18, 20];
  const BALL_DROPS = [0, 10, -2, 12, 0, 8, 2, 11];
  /* Palette order is a role (blocks/_lamp-palette.liquid): 1 frame, 2 balls, 3 flowers, 4 details, 5 extra balls. */
  const BALL_TONES = [2, 3, 2, 5, 2, 3, 2, 5, 3];
  const STAR_INSET = 18;

  const svg = (tag, attributes = {}) => {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [name, value] of Object.entries(attributes)) el.setAttribute(name, String(value));
    return el;
  };

  const starPath = (cx, cy, r, points = 5, inner = 0.45) => {
    let d = '';
    for (let i = 0; i < points * 2; i++) {
      const radius = i % 2 ? r * inner : r;
      const angle = (Math.PI / points) * i - Math.PI / 2;
      d += `${i ? 'L' : 'M'}${(cx + radius * Math.cos(angle)).toFixed(1)} ${(cy + radius * Math.sin(angle)).toFixed(1)}`;
    }
    return `${d}Z`;
  };

  class LampConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.form = this.querySelector('[data-lamp-form]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.alert = this.querySelector('[data-alert]');
      this.designMode = this.dataset.designMode === 'true';
      this.current = 0;
      this.sessionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      this.distributeOptions();
      this.setupArt();
      this.bind();
      this.applyRules();
      this.show(0);
      this.guardOtherForms();
    }

    /* Child option blocks render into a hidden pool; move each into its step. */
    distributeOptions() {
      this.querySelectorAll('[data-option-pool] [data-lamp-option]').forEach((option) => {
        const target = this.querySelector(`[data-options-for="${option.dataset.lampOption}"]`);
        if (target) target.append(option);
      });
    }

    bind() {
      this.querySelector('[data-next]').addEventListener('click', () => this.next());
      this.querySelector('[data-back]').addEventListener('click', () => this.back());

      this.form.addEventListener('change', (event) => {
        const input = event.target;
        if (input.name === 'properties[Form]') this.applyRules();
        if (input.matches('[data-contact-choice]')) this.updateContactFields();
        const step = input.closest('[data-step]');
        if (step) this.updatePreview(step);
        if (input.type === 'radio') this.cheer();
        if (input.name && !PRIVATE_ANSWERS.includes(input.name.slice(11, -1))) {
          this.track('lamp_configurator_option_selected', { answer: input.name.slice(11, -1), value: input.value });
        }
        this.alert.textContent = '';
        this.showPhoto = false;
        this.renderArt();
        this.updateNavState();
      });
      this.form.addEventListener('input', (event) => {
        if (event.target.name === 'properties[Lampens navn]') this.renderArt();
        this.updateNavState();
      });

      this.form.addEventListener('submit', (event) => {
        const invalid = this.activeSteps().find((step) => !this.validate(step));
        if (this.steps[this.current].dataset.step !== 'review' || invalid) {
          event.preventDefault();
          if (invalid) this.show(this.steps.indexOf(invalid));
          return;
        }
        this.track('lamp_configurator_submitted');
      });

      // In the theme editor, jump to the step of the block being edited.
      document.addEventListener('shopify:block:select', (event) => {
        const el = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
        const step = el && el.closest('[data-step]');
        if (step) this.show(this.steps.indexOf(step));
      });
    }

    cheer() {
      const line = this.querySelector('[data-cheer]');
      let text;
      do text = CHEERS[Math.floor(Math.random() * CHEERS.length)];
      while (CHEERS.length > 1 && text === line.textContent);
      line.textContent = text;
    }

    shapeKey() {
      const checked = this.form.querySelector('input[name="properties[Form]"]:checked');
      return checked ? checked.dataset.shapeKey : '';
    }

    isSkipped(step) {
      const key = step.dataset.step;
      if (this.shapeKey() === 'rocket' && ROCKET_SKIPS.includes(key)) return true;
      const options = step.querySelector('[data-options-for]');
      if (options && !this.designMode && !options.querySelector('[data-lamp-option]')) return true;
      return false;
    }

    activeSteps() {
      return this.steps.filter((step) => !this.isSkipped(step));
    }

    /* Enable/disable steps and options from the chosen shape. Disabled inputs are not sent to the cart. */
    applyRules() {
      const cloudOnly = CLOUD_SHAPES.includes(this.shapeKey());

      this.steps.forEach((step) => {
        const skipped = this.isSkipped(step);
        step.querySelectorAll('[data-lamp-option] input').forEach((input) => {
          input.disabled = skipped;
        });
      });

      this.querySelectorAll('[data-lamp-option="balls"]').forEach((option) => {
        const allowed = !cloudOnly || option.dataset.cloudOk === 'true';
        const input = option.querySelector('input');
        option.hidden = !allowed;
        if (!allowed) {
          input.disabled = true;
          input.checked = false;
        }
      });

      const balls = this.querySelector('[data-step="balls"]');
      if (balls) this.updatePreview(balls);
    }

    updateContactFields() {
      const choice = this.form.querySelector('[data-contact-choice]').value;
      this.querySelectorAll('[data-contact]').forEach((field) => {
        const active = field.dataset.contact === choice;
        const input = field.querySelector('input');
        field.hidden = !active;
        input.disabled = !active;
        input.required = active;
      });
    }

    /* Photo preview per step (preview style "Eksempelbilleder"). */
    updatePreview(step) {
      const preview = step.querySelector('[data-preview]');
      if (!preview) return;
      const checked = step.querySelector('input:checked:not(:disabled)');
      const url = checked && checked.dataset.example;
      const img = preview.querySelector('[data-preview-image]');
      const placeholder = preview.querySelector('[data-preview-placeholder]');
      img.hidden = !url;
      placeholder.hidden = Boolean(url);
      // The "example from an earlier lamp" note only makes sense under a real photo.
      const caption = preview.querySelector('figcaption');
      if (caption) caption.hidden = !url;
      if (url) {
        img.src = url;
        img.alt = `Eksempel: ${checked.value}`;
      } else if (!checked) {
        placeholder.textContent = 'Vælg en mulighed for at se et eksempel';
      } else if (checked.dataset.noExample) {
        placeholder.textContent = `Der er intet eksempel for "${checked.value}".`;
      } else {
        placeholder.textContent = preview.dataset.missingText || 'Eksempelbillede kommer snart';
      }
    }

    /* ---------- Lamp drawing (preview style "Tegning") ---------- */

    setupArt() {
      const art = this.querySelector('[data-lamp-art]');
      if (!art) return;
      const part = (name) => art.querySelector(`[data-art-${name}]`);
      this.art = {
        root: art,
        svg: part('svg'),
        outline: part('outline'),
        geometry: part('geometry'),
        detail: part('detail'),
        glitter: part('glitter'),
        stars: part('stars'),
        layers: part('layers'),
        lights: part('lights'),
        sparkles: part('sparkles'),
        figure: part('figure'),
        name: part('name'),
        balls: part('balls'),
        photo: part('photo'),
        photoNote: part('photo-note'),
        hint: part('hint'),
        toggle: part('toggle'),
      };
      this.artCache = new Map();
      this.showPhoto = false;

      this.art.toggle.addEventListener('click', () => {
        this.showPhoto = !this.showPhoto;
        this.renderArt();
      });
      // The name is measured to fit its space, so measure again once the handwriting font is in.
      if (document.fonts) document.fonts.ready.then(() => this.renderArt());
    }

    /** @param {string} answer - Cart property name; returns its chosen, enabled radio */
    chosen(answer) {
      return this.form.querySelector(`input[name="properties[${answer}]"]:checked:not(:disabled)`);
    }

    /* The chosen shape, or the plain cloud until one is chosen. */
    artShape() {
      const inputs = [...this.form.querySelectorAll('input[name="properties[Form]"][data-shape-path]')];
      const input =
        inputs.find((i) => i.checked) || inputs.find((i) => i.dataset.shapeKey === 'cloud') || inputs[0];
      if (!input) return null;
      let layout = {};
      try {
        layout = JSON.parse(input.dataset.shapeLayout);
      } catch {
        // Keep the outline even if the layout is unreadable.
      }
      return { key: input.dataset.shapeKey, path: input.dataset.shapePath, layout };
    }

    inFill(x, y) {
      try {
        return this.art.geometry.isPointInFill(new DOMPoint(x, y));
      } catch {
        return true;
      }
    }

    /* Points that depend only on the outline: lights and sparkles on the frame, relief stars inside. */
    shapeGeometry(shape) {
      if (this.artCache.has(shape.key)) return this.artCache.get(shape.key);
      const path = this.art.geometry;
      path.setAttribute('d', shape.path);

      const lights = [];
      const total = path.getTotalLength();
      const count = Math.max(8, Math.round(total / LIGHT_SPACING));
      for (let i = 0; i < count; i++) {
        const at = (total / count) * i;
        const p = path.getPointAtLength(at);
        const q = path.getPointAtLength(Math.min(total, at + 1));
        const length = Math.hypot(q.x - p.x, q.y - p.y) || 1;
        let nx = -(q.y - p.y) / length;
        let ny = (q.x - p.x) / length;
        // Point the normal outwards, onto the frame.
        if (this.inFill(p.x + nx * LIGHT_OFFSET, p.y + ny * LIGHT_OFFSET)) {
          nx = -nx;
          ny = -ny;
        }
        lights.push({ x: p.x + nx * LIGHT_OFFSET, y: p.y + ny * LIGHT_OFFSET });
      }

      let stars = '';
      let index = 0;
      for (let y = 40; y < 400; y += 44) {
        for (let x = 30; x < 600; x += 50) {
          index += 1;
          const sx = x + ((index * 37) % 17) - 8 + (Math.floor(y / 44) % 2) * 25;
          const sy = y + ((index * 23) % 13) - 6;
          const inset = STAR_INSET;
          if (
            this.inFill(sx, sy) &&
            this.inFill(sx - inset, sy) &&
            this.inFill(sx + inset, sy) &&
            this.inFill(sx, sy - inset) &&
            this.inFill(sx, sy + inset)
          ) {
            stars += starPath(sx, sy, index % 3 ? 8 : 11);
          }
        }
      }

      const sparkles = lights
        .filter((_, i) => i % 4 === 1)
        .map((p) => starPath(p.x, p.y, 9, 4, 0.3))
        .join('');

      const geometry = { lights, stars, sparkles };
      this.artCache.set(shape.key, geometry);
      return geometry;
    }

    drawShape(shape) {
      const { outline, detail, layers, lights, stars, sparkles } = this.art;
      outline.setAttribute('d', shape.path);
      const geometry = this.shapeGeometry(shape);

      if (shape.layout.detail) detail.setAttribute('d', shape.layout.detail);
      else detail.removeAttribute('d');

      layers.replaceChildren(
        ...(shape.layout.layers || []).map(([scale, cx, cy]) =>
          svg('use', {
            href: `#${outline.id}`,
            class: 'lamp-art__layer',
            transform: `translate(${cx} ${cy}) scale(${scale}) translate(${-cx} ${-cy})`,
          })
        )
      );

      lights.replaceChildren(
        ...geometry.lights.map((p, i) =>
          svg('circle', { class: 'lamp-art__light', cx: p.x.toFixed(1), cy: p.y.toFixed(1), r: 4.2, style: `--i: ${i}` })
        )
      );
      stars.replaceChildren(svg('path', { class: 'lamp-art__star', d: geometry.stars }));
      sparkles.replaceChildren(svg('path', { class: 'lamp-art__sparkle', d: geometry.sparkles }));
    }

    /* Palette colors as custom properties, so the drawing and the small option drawings follow them. */
    applyPalette() {
      const palette = this.chosen('Farvepalet');
      const colors = palette && palette.dataset.colors ? palette.dataset.colors.split(',').filter(Boolean) : [];
      for (let i = 0; i < PALETTE_SIZE; i++) {
        if (colors.length) this.style.setProperty(`--lamp-c${i + 1}`, colors[i % colors.length]);
        else this.style.removeProperty(`--lamp-c${i + 1}`);
      }
      if (colors.length) this.style.setProperty('--lamp-body', BODY_COLOR);
      else this.style.removeProperty('--lamp-body');
    }

    addBall(group, shine, x, y, r, tone) {
      if (!this.inFill(x, y)) return;
      const ball = svg('g');
      ball.append(
        svg('circle', { class: `lamp-art__ball lamp-art__fill--${tone}`, cx: x, cy: y, r }),
        svg('circle', { cx: x, cy: y, r, fill: shine })
      );
      group.append(ball);
    }

    addFlower(group, x, y, tone) {
      if (!this.inFill(x, y)) return;
      const flower = svg('g', { transform: `translate(${x} ${y})` });
      flower.append(
        svg('ellipse', { class: 'lamp-art__leaf', cx: -9, cy: 6, rx: 8, ry: 3.5, transform: 'rotate(-30)' }),
        svg('ellipse', { class: 'lamp-art__leaf', cx: 9, cy: 6, rx: 8, ry: 3.5, transform: 'rotate(30)' })
      );
      for (let k = 0; k < 5; k++) {
        const angle = ((Math.PI * 2) / 5) * k - Math.PI / 2;
        flower.append(
          svg('circle', {
            class: `lamp-art__fill--${tone}`,
            cx: (Math.cos(angle) * 6).toFixed(1),
            cy: (Math.sin(angle) * 6).toFixed(1),
            r: 5.2,
          })
        );
      }
      flower.append(svg('circle', { class: 'lamp-art__flower-center', r: 3.4 }));
      group.append(flower);
    }

    drawBalls(kind, row) {
      const group = this.art.balls;
      const shine = group.dataset.shine;
      group.replaceChildren();
      if (!row || !kind || kind === 'none') return;
      const [x0, x1, y] = row;

      if (kind === 'three') {
        const span = x1 - x0;
        const centers = [0.3, 0.5, 0.7].map((f) => x0 + span * f);
        for (let x = x0, i = 0; x <= x1; x += 24, i++) {
          if (centers.some((c) => Math.abs(c - x) < 36)) continue;
          this.addFlower(group, x, y + (i % 2 ? 10 : -2), i % 3 ? 3 : 5);
        }
        centers.slice(0, 2).forEach((c, i) => this.addFlower(group, (c + centers[i + 1]) / 2, y - 34, i ? 3 : 5));
        centers.forEach((c, i) => this.addBall(group, shine, c, y - 4, 30, [3, 2, 5][i]));
        return;
      }

      const row1 = [];
      for (let x = x0, i = 0; x <= x1; x += 40, i++) row1.push({ x, y: y + BALL_DROPS[i % BALL_DROPS.length], i });
      for (let i = 0; i < row1.length - 1; i++) {
        const x = (row1[i].x + row1[i + 1].x) / 2;
        if (kind === 'flowers') this.addFlower(group, x, y - 24, i % 2 ? 5 : 3);
        else if (i % 2 === 0) this.addBall(group, shine, x, y - 26, 14, i % 4 ? 5 : 3);
      }
      row1.forEach(({ x, y: by, i }) =>
        this.addBall(group, shine, x, by, BALL_RADII[i % BALL_RADII.length], BALL_TONES[i % BALL_TONES.length])
      );
    }

    drawName(layout) {
      const text = this.art.name;
      const input = this.form.querySelector('[name="properties[Lampens navn]"]');
      const value = input ? input.value.trim().toUpperCase() : '';
      const [x, y, maxWidth, size] = layout.name || [300, 250, 200, 42];
      text.setAttribute('x', x);
      text.setAttribute('y', y);
      text.setAttribute('font-size', size);
      text.textContent = value || 'DIT NAVN';
      text.classList.toggle('is-placeholder', !value);

      const finish = this.chosen('Bogstaver');
      const finishName = finish ? finish.value.toLowerCase() : '';
      if (finishName.includes('sølv') || finishName.includes('silver')) text.style.fill = text.dataset.silver;
      else if (finish) text.style.fill = text.dataset.gold;
      else text.style.removeProperty('fill');

      // Long names shrink to fit their space on the lamp.
      const width = text.getComputedTextLength();
      if (width > maxWidth) text.setAttribute('font-size', Math.max(16, (size * maxWidth) / width).toFixed(1));
    }

    drawFigure(layout) {
      const image = this.art.figure;
      const figure = this.chosen('Figur');
      const url = figure && figure.dataset.illustration;
      const visible = Boolean(url && layout.figure);
      image.toggleAttribute('hidden', !visible);
      if (!visible) return;
      const [x, y, size] = layout.figure;
      image.setAttribute('href', url);
      for (const [name, value] of Object.entries({ x, y, width: size, height: size })) image.setAttribute(name, value);
    }

    renderArt() {
      if (!this.art) return;
      const shape = this.artShape();
      if (!shape) return;
      if (shape.key !== this.drawnShape) {
        this.drawnShape = shape.key;
        this.drawShape(shape);
      }

      this.applyPalette();
      const art = (answer) => {
        const input = this.chosen(answer);
        return input ? input.dataset.art : '';
      };
      const light = art('Lys');
      this.art.lights.setAttribute('class', `lamp-art__lights lamp-art__lights--${light || 'off'}`);
      this.art.stars.toggleAttribute('hidden', art('Relief') !== 'stars');
      const glitter = art('Glimmer') === 'brocade';
      this.art.glitter.toggleAttribute('hidden', !glitter);
      this.art.sparkles.toggleAttribute('hidden', !glitter);
      this.drawBalls(art('Kugler og blomster'), shape.layout.balls);
      this.drawFigure(shape.layout);
      this.drawName(shape.layout);

      const described = ['Form', 'Farvepalet', 'Kugler og blomster', 'Figur']
        .map((answer) => this.chosen(answer))
        .filter(Boolean)
        .map((input) => input.dataset.label || input.value);
      this.art.svg.setAttribute('aria-label', ['Tegning af din lampe', described.join(', ')].filter(Boolean).join(': '));

      this.renderArtCaption();
    }

    /* Hint for the current step, and a button to swap the drawing for a real example photo. */
    renderArtCaption() {
      const step = this.steps[this.current];
      if (!step) return;
      const { hint, toggle, photo, svg: drawing } = this.art;
      const checked = step.querySelector('input[type="radio"]:checked:not(:disabled)');
      let text = step.dataset.drawingHint || '';
      if (checked && step.dataset.step === 'figure' && !checked.dataset.illustration && step.dataset.missingText) {
        text = step.dataset.missingText;
      }
      hint.textContent = text;
      hint.hidden = !text;

      const example = checked && checked.dataset.example;
      if (!example) this.showPhoto = false;
      toggle.hidden = !example;
      toggle.textContent = this.showPhoto ? 'Vis tegningen igen' : 'Se et rigtigt eksempel';
      toggle.setAttribute('aria-pressed', String(this.showPhoto));
      photo.hidden = !this.showPhoto;
      if (this.art.photoNote) this.art.photoNote.hidden = !this.showPhoto;
      drawing.toggleAttribute('hidden', this.showPhoto);
      if (this.showPhoto) {
        photo.src = example;
        photo.alt = `Eksempel fra en lampe, vi har lavet: ${checked.dataset.label || checked.value}`;
      }
    }

    /* ---------- Marketing events ---------- */

    /*
     * Publish a Shopify customer event with the design so far, so custom pixels (Settings → Customer
     * events) can collect unfinished designs too. Shopify applies the visitor's cookie consent.
     */
    track(name, extra = {}) {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (this.designMode || !analytics || typeof analytics.publish !== 'function') return;

      const design = {};
      for (const field of this.form.querySelectorAll('[name^="properties["]:not(:disabled)')) {
        const answer = field.name.slice(11, -1);
        if (PRIVATE_ANSWERS.includes(answer)) continue;
        if (field.type === 'radio' && !field.checked) continue;
        if (field.value) design[answer] = field.value;
      }
      const active = this.activeSteps();
      const step = this.steps[this.current];
      const nameInput = this.form.querySelector('[name="properties[Lampens navn]"]');

      try {
        analytics.publish(name, {
          session_id: this.sessionId,
          product_id: this.dataset.productId,
          step: step.dataset.step,
          step_number: active.indexOf(step) + 1,
          steps_total: active.length,
          name_entered: Boolean(nameInput && nameInput.value.trim()),
          design,
          ...extra,
        });
      } catch {
        // Tracking must never break the configurator.
      }
    }

    /* ---------- Steps ---------- */

    /* With report = false this only checks, without showing messages. */
    validate(step, report = true) {
      const choices = step.querySelectorAll('[data-choices]');
      for (const group of choices) {
        const inputs = [...group.querySelectorAll('input[type="radio"]:not(:disabled)')];
        if (inputs.length && !inputs.some((input) => input.checked)) {
          if (report) this.alert.textContent = 'Hov! Vælg lige en af mulighederne, før vi går videre 🙂';
          return false;
        }
      }
      for (const field of step.querySelectorAll('input:not([type="radio"]):not(:disabled), select')) {
        if (!field.checkValidity()) {
          if (report) {
            field.reportValidity();
            this.alert.textContent = 'Hov! Der mangler lige noget her 🙂';
          }
          return false;
        }
      }
      if (report) this.alert.textContent = '';
      return true;
    }

    /* Dim Next/submit until the current step is filled in. They stay clickable to explain what is missing. */
    updateNavState() {
      const complete = this.validate(this.steps[this.current], false);
      ['[data-next]', '[data-submit]'].forEach((selector) => {
        const button = this.querySelector(selector);
        button.classList.toggle('is-incomplete', !complete);
        button.setAttribute('aria-disabled', String(!complete));
      });
    }

    next() {
      const step = this.steps[this.current];
      if (!this.validate(step)) return;
      const following = this.steps.slice(this.current + 1).find((s) => !this.isSkipped(s));
      if (following) this.show(this.steps.indexOf(following));
    }

    back() {
      const previous = this.steps
        .slice(0, this.current)
        .reverse()
        .find((s) => !this.isSkipped(s));
      if (previous) this.show(this.steps.indexOf(previous));
    }

    show(index) {
      this.current = index;
      const active = this.activeSteps();
      const position = active.indexOf(this.steps[index]);
      const isReview = this.steps[index].dataset.step === 'review';

      this.steps.forEach((step, i) => {
        step.hidden = i !== index;
      });
      active.forEach((step, i) => {
        const number = step.querySelector('[data-step-number]');
        if (number) number.textContent = `${i + 1}. `;
      });

      this.querySelector('[data-progress]').textContent = `Trin ${position + 1} af ${active.length}`;
      this.querySelector('[data-back]').hidden = position <= 0;
      this.querySelector('[data-next]').hidden = isReview;
      this.querySelector('[data-submit]').hidden = !isReview;
      this.alert.textContent = '';
      this.querySelector('[data-cheer]').textContent = '';
      const following = active[position + 1];
      this.querySelector('[data-next]').textContent = following
        ? `Videre: ${following.dataset.stepLabel} →`
        : 'Videre →';

      this.updatePreview(this.steps[index]);
      this.showPhoto = false;
      this.renderArt();
      if (isReview) this.renderSummary();
      this.updateNavState();
      this.renderStepper(active, position);
      this.track('lamp_configurator_step_viewed');

      const top = this.getBoundingClientRect().top;
      if (top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* Connected circles for the steps this customer will see. Earlier steps can be clicked to go back. */
    renderStepper(active, position) {
      const list = this.querySelector('[data-stepper]');
      list.replaceChildren();
      active.forEach((step, i) => {
        const item = document.createElement('li');
        const state = i < position ? 'done' : i === position ? 'current' : 'upcoming';
        item.className = `lamp-stepper__item lamp-stepper__item--${state}`;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'lamp-stepper__button';
        button.disabled = state !== 'done';
        if (state === 'current') button.setAttribute('aria-current', 'step');
        button.innerHTML = '<span class="lamp-stepper__circle"></span><span class="lamp-stepper__label"></span>';
        button.querySelector('.lamp-stepper__circle').textContent = state === 'done' ? '✓' : String(i + 1);
        button.querySelector('.lamp-stepper__label').textContent = `Trin ${i + 1}: ${step.dataset.stepLabel}`;
        button.addEventListener('click', () => this.show(this.steps.indexOf(step)));
        item.append(button);
        list.append(item);
      });
      const current = active[position];
      this.querySelector('[data-stepper-caption]').textContent = current
        ? `Trin ${position + 1} af ${active.length}: ${current.dataset.stepLabel}`
        : '';
    }

    /* One row per answer, with a button back to the step it came from. */
    renderSummary() {
      const out = this.querySelector('[data-summary]');
      out.replaceChildren();
      const fields = this.form.querySelectorAll('[name^="properties["]:not(:disabled)');
      fields.forEach((field) => {
        if (field.closest('[data-step="review"]')) return;
        if (field.type === 'radio' && !field.checked) return;
        if (!field.value) return;

        const row = document.createElement('div');
        row.className = 'lamp-config__summary-row';
        const option = field.closest('.lamp-option');
        const swatches = option && option.querySelector('.lamp-swatches');
        // With the drawing, show the small drawings or the photo on the card; with photo previews, the example photos.
        const mini = this.art && option && option.querySelector('.lamp-option__art svg, .lamp-option__shape svg');
        const thumb = option && option.querySelector('.lamp-option__thumb img');
        const picture = this.art
          ? (thumb && (thumb.currentSrc || thumb.src)) || field.dataset.illustration
          : field.dataset.example;
        if (swatches) {
          const copy = swatches.cloneNode(true);
          copy.hidden = false;
          row.append(copy);
        } else if (mini) {
          const holder = document.createElement('span');
          holder.className = 'lamp-config__summary-art';
          holder.append(mini.cloneNode(true));
          row.append(holder);
        } else if (picture) {
          const img = document.createElement('img');
          img.src = picture;
          img.alt = '';
          row.append(img);
        }
        const name = field.name.slice(11, -1);
        const text = document.createElement('span');
        text.className = 'lamp-config__summary-text';
        text.textContent = `${name}: ${field.dataset.label || field.value}`;
        row.append(text);

        const step = field.closest('[data-step]');
        if (step) {
          const change = document.createElement('button');
          change.type = 'button';
          change.className = 'lamp-config__summary-change';
          change.textContent = 'Ændre';
          change.setAttribute('aria-label', `Ændre ${name}`);
          change.addEventListener('click', () => this.show(this.steps.indexOf(step)));
          row.append(change);
        }
        out.append(row);
      });
    }

    /* The normal product buy buttons on this template must not bypass the configurator. */
    guardOtherForms() {
      document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
        if (form === this.form) return;
        form
          .querySelectorAll('button[type="submit"], input[type="submit"], shopify-accelerated-checkout')
          .forEach((button) => {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          });
      });
    }
  }

  customElements.define('lamp-configurator', LampConfigurator);
})();
